import {
	type MouseEvent,
	type ReactNode,
	type RefObject,
	type SyntheticEvent,
	useEffect,
	useId,
	useRef,
} from "react";
import { Close } from "../icons/icons";
import { cx } from "../lib/cx";
import { useUiStrings } from "../strings/ui-strings";

export interface ModalProps {
	open: boolean;
	/** Fires for Escape, the backdrop, and the × — the app decides whether to honour it (see `dismissible`). */
	onClose: () => void;
	title: ReactNode;
	description?: ReactNode;
	children: ReactNode;
	/** Right-aligned action row. Use `ModalActions` for the standard layout. */
	footer?: ReactNode;
	size?: "sm" | "md" | "lg" | "xl";
	/**
	 * False while a request is in flight, or when closing would lose
	 * something the user can't get back (a generated password). Escape and
	 * backdrop clicks are then ignored and the × is hidden.
	 */
	dismissible?: boolean;
	/** Element to focus on open. Defaults to the first focusable thing in the body. */
	initialFocusRef?: RefObject<HTMLElement | null>;
	/** aria-label of the ×. Default from the provider ("Close"). */
	closeLabel?: string;
	className?: string;
}

// md = her 520px dialog. Phones get the full-width bottom sheet whatever the size.
const SIZE = { sm: "sm:max-w-[26rem]", md: "sm:max-w-[32.5rem]", lg: "sm:max-w-[44rem]", xl: "sm:max-w-[78rem]" } as const;

/**
 * Modal — a native <dialog> so focus trapping, Escape, inert background and
 * focus-return-to-opener are the browser's job, not ours. `open` is the only
 * state; the component mirrors it onto `showModal()` / `close()`.
 *
 * Layering: the app header is z-10, the sidebar z-20, Select popovers z-30.
 * The dialog lives in the top layer, so it needs no z-index at all.
 */
export function Modal({
	open,
	onClose,
	title,
	description,
	children,
	footer,
	size = "md",
	dismissible = true,
	initialFocusRef,
	closeLabel,
	className,
}: ModalProps) {
	const strings = useUiStrings().modal;
	const ref = useRef<HTMLDialogElement>(null);
	const titleId = useId();
	const descId = useId();

	// Keep the latest callbacks without re-running the open/close effect.
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;
	const dismissibleRef = useRef(dismissible);
	dismissibleRef.current = dismissible;

	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		if (open && !el.open) {
			el.showModal();
			const target = initialFocusRef?.current ?? el.querySelector<HTMLElement>("[data-autofocus], input, select, textarea, button:not([data-modal-close])");
			target?.focus();
		} else if (!open && el.open) {
			el.close();
		}
	}, [open, initialFocusRef]);

	// Escape → the browser fires `cancel`; veto it when not dismissible, else route through onClose.
	const onCancel = (e: SyntheticEvent<HTMLDialogElement>) => {
		e.preventDefault();
		if (dismissibleRef.current) onCloseRef.current();
	};

	// A click on the backdrop lands on the <dialog> itself, not on a descendant.
	const onBackdropClick = (e: MouseEvent<HTMLDialogElement>) => {
		if (e.target === e.currentTarget && dismissibleRef.current) onCloseRef.current();
	};

	return (
		<dialog
			ref={ref}
			onCancel={onCancel}
			onClick={onBackdropClick}
			aria-labelledby={titleId}
			aria-describedby={description ? descId : undefined}
			className={cx(
				// The dialog box is transparent and full-bleed; the card inside is the visible surface.
				// The designer's `.sheet`: a bottom sheet on phones, a centred dialog from sm up.
				"m-0 h-full max-h-none w-full max-w-none bg-transparent p-0 text-base-content",
				"backdrop:bg-[rgb(9_10_40/0.48)]",
				"open:grid open:place-items-end open:sm:place-items-center",
				"sm:p-[1.5rem]",
			)}
		>
			<div
				className={cx(
					"flex w-full flex-col overflow-hidden rounded-t-box bg-base-100 pb-[env(safe-area-inset-bottom)] sm:rounded-box",
					"shadow-[0_1rem_2.5rem_-1.375rem_rgba(9,10,40,0.8)]",
					"max-h-[90dvh] sm:max-h-[min(45rem,86dvh)]",
					"motion-safe:animate-[modal-in_180ms_cubic-bezier(0.2,0.8,0.2,1)]",
					SIZE[size],
					className,
				)}
			>
				<header className="flex items-start gap-[0.5rem] pt-[0.5rem] pr-[0.5rem] pb-[0.5rem] pl-[1rem] sm:pt-[1rem] sm:pr-[0.75rem] sm:pb-[0.625rem] sm:pl-[1.5rem]">
					<div className="min-w-0 flex-1 pt-[0.5rem] sm:pt-[0.25rem]">
						<h2 id={titleId} className="text-[1.0625rem] leading-[1.3] font-semibold text-gms-navy-ink">
							{title}
						</h2>
						{description && (
							<p id={descId} className="mt-[0.125rem] text-[0.78125rem] leading-snug text-gms-ink-3">
								{description}
							</p>
						)}
					</div>
					{dismissible && (
						<button
							type="button"
							data-modal-close
							aria-label={closeLabel ?? strings.close}
							onClick={onClose}
							className="grid size-[2.75rem] shrink-0 cursor-pointer place-items-center rounded-[0.5rem] text-gms-navy-ink outline-none transition-colors hover:bg-base-200 focus-visible:ring-2 focus-visible:ring-gms-navy/40 [&>svg]:size-[1.25rem]"
						>
							<Close />
						</button>
					)}
				</header>

				<div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-[1rem] pt-[0.25rem] pb-[1rem] sm:px-[1.5rem] sm:pb-[1.5rem]">
					{children}
				</div>

				{footer && (
					<footer className="border-t border-gms-line px-[1rem] py-[0.75rem] sm:px-[1.5rem]">{footer}</footer>
				)}
			</div>
		</dialog>
	);
}

/** Standard action row: anything in `start` hugs the left, the rest hugs the right. */
export function ModalActions({ start, children }: { start?: ReactNode; children: ReactNode }) {
	return (
		<div className="flex flex-wrap items-center justify-between gap-[0.5rem]">
			<div className="flex items-center gap-[0.5rem]">{start}</div>
			<div className="ml-auto flex flex-wrap items-center justify-end gap-[0.5rem]">{children}</div>
		</div>
	);
}
